import { useEffect, useState, memo, useCallback, useMemo, useContext } from 'react';

import ReferencesSection from './ReferencesSection';
import { validateAction } from '../utils';
import { useStepSubmitHandler } from '../../../hooks/useStepSubmitHandler';
import { StepWidthWrapper, StepWidth } from '../../../step/StepWidthWrapper';
import { StepCard } from '../../../step/StepCard';
import { ResponseTemplatingButton } from '../../../step/ResponseTemplatingButton';
import { CommentButton } from '../../../feedback/CommentButton';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { useTrans } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useTrans';
import { produce } from 'immer';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import {
  useChatConfig,
  useChatHistory,
  useChatStoreExtract,
  useMostRecentPrompt,
  useStepParams,
  useStepResponse,
} from '../../../../Chat/hooks/ChatStore.hooks';
import { Button, Tooltip } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../../InteractionStep';
import { CardAlert } from '../../../TempComponents';
import {
  InteractionStepType,
  DEFAULT_SEARCH_AUTOCOMPLETE_TITLE,
  DEFAULT_SEARCH_AUTOCOMPLETE_SUBTITLE,
  SearchResponseV2,
  MessageRole,
} from '@scale/shared/types/chat';
import {
  SearchReferencesStepResponse,
  SearchResultAction,
} from '@scale/shared/types/chat/response';
import _ from 'lodash';
import { PinButton } from '../../../step/PinButton';

const SNIPPET_REFERENCES_TOOLTIP =
  'Please select at least one search snippet in the main reference and one search snippet in an  reference';
const NO_REFERENCES_TOOLTIP = 'Please add at least one  reference';
const INCOMPLETE_REFERENCES_TOOLTIP =
  'Please select at least one search snippet in each  reference or delete empty references';
const INVALID_ACTION_TOOLTIP = 'Please make sure all actions are valid';
const MISSING_ACTION_TOOLTIP =
  'Please make sure each selected reference contains at least one action';

const SearchReferencesTaskComponent: InteractionStepTaskComponent =
  // eslint-disable-next-line complexity
  ({ readOnly, complete, responseIndex, isPinnedView }) => {
    const taskContext = useContext(ChatTaskContext);
    const { t } = useTrans();
    const config = useChatConfig();
    const chatHistory = useChatHistory(responseIndex);
    const step = useStepParams<InteractionStepType.SearchReferences>(responseIndex);
    const response = useStepResponse<SearchReferencesStepResponse>(responseIndex);
    const chatPrompt = useMostRecentPrompt(responseIndex);
    const { onSubmit } = useStepSubmitHandler({
      responseIndex,
    });
    const markStepAsIncompletedByIndex = useChatStoreExtract(
      state => state.markStepAsIncompletedByIndex,
    );
    const markStepAsCompletedByIndex = useChatStoreExtract(
      state => state.markStepAsCompletedByIndex,
    );

    const [submittedPrompt, setSubmittedPrompt] = useState<string | undefined>(undefined);

    // Extract the relevant data from the existing response if it exists
    const [references, setReferences] = useState<SearchResponseV2[]>(
      response?.context?.references ?? [],
    );

    const allSearchResults = useMemo(() => {
      return references.map(r => r.searchResults).flat();
    }, [references]);

    const handleAddSearchResultAction = useCallback(
      (index: number, action: SearchResultAction, ReferenceIndex: number) => {
        setReferences(prev =>
          produce(prev, draft => {
            if (!_.isArray(draft[ReferenceIndex].searchResults[index].actions)) {
              draft[ReferenceIndex].searchResults[index].actions = [action];
            } else {
              draft[ReferenceIndex].searchResults[index].actions!.push(action);
            }
          }),
        );
      },
      [],
    );

    const handleEditSearchResultAction = useCallback(
      (index: number, actionIndex: number, action: SearchResultAction, ReferenceIndex: number) => {
        setReferences(prev =>
          produce(prev, draft => {
            if (draft.length > 0 && draft[ReferenceIndex].searchResults[index].actions) {
              draft[ReferenceIndex].searchResults[index].actions![actionIndex] = action;
            }
          }),
        );
      },
      [],
    );

    const handleRemoveSearchResultAction = useCallback(
      (index: number, actionIndex: number, ReferenceIndex: number) => {
        setReferences(prev =>
          produce(prev, draft => {
            if (draft.length > 0 && draft[ReferenceIndex].searchResults[index].actions) {
              draft[ReferenceIndex].searchResults[index].actions!.splice(actionIndex, 1);
            }
          }),
        );
      },
      [],
    );

    const handleActionReorder = useCallback(
      (index: number, sourceIndex: number, destinationIndex: number, ReferenceIndex: number) => {
        setReferences(prev =>
          produce(prev, draft => {
            if (draft.length > 0 && draft[ReferenceIndex].searchResults[index].actions) {
              const [removed] = draft[ReferenceIndex].searchResults[index].actions!.splice(
                sourceIndex,
                1,
              );
              draft[ReferenceIndex].searchResults[index].actions!.splice(
                destinationIndex,
                0,
                removed,
              );
            }
          }),
        );
      },
      [],
    );

    const areReferencesComplete = useMemo(() => {
      return references.every(
        reference =>
          reference.searchTerm.length > 0 &&
          reference.searchResults.length > 0 &&
          _.some(reference.searchResults, ['isChecked', true]),
      );
    }, [references]);

    //  references may be optional, but at least one snippet in the main search query is always required
    const areAllSearchResultsValid = useMemo(() => {
      return _.some(references, reference => _.some(reference.searchResults, ['isChecked', true]));
    }, [references]);

    const submitTooltip = useMemo(() => {
      if (!references.length) {
        return NO_REFERENCES_TOOLTIP;
      }
      if (!areReferencesComplete) {
        return INCOMPLETE_REFERENCES_TOOLTIP;
      }
      if (!areAllSearchResultsValid) {
        return SNIPPET_REFERENCES_TOOLTIP;
      }

      if (
        _.some(
          allSearchResults.map(r => r.actions ?? []).flat(),
          action => !validateAction(action).isValid,
        )
      ) {
        return INVALID_ACTION_TOOLTIP;
      }

      if (
        _.some(
          allSearchResults,
          searchResult => searchResult.isChecked && (searchResult.actions ?? []).length === 0,
        )
      ) {
        return MISSING_ACTION_TOOLTIP;
      }

      return '';
    }, [references.length, areReferencesComplete, areAllSearchResultsValid, allSearchResults]);

    // Reactivate step if the current response differs from the existing response
    const resultChanged = useMemo(() => {
      return !_.isEqual(
        {
          prompt: chatPrompt,
          references,
        },
        response?.context,
      );
    }, [response, chatPrompt, references]);

    /* Explicitly mark the step as incomplete / complete if an initial response
     * exists and the current response is different.
     */
    useEffect(() => {
      const canUpdateCompletedState = response && !_.isNil(responseIndex);
      if (canUpdateCompletedState && resultChanged) {
        markStepAsIncompletedByIndex(responseIndex);
      } else if (canUpdateCompletedState && !resultChanged) {
        markStepAsCompletedByIndex(responseIndex);
      }
    }, [
      response,
      resultChanged,
      responseIndex,
      markStepAsIncompletedByIndex,
      markStepAsCompletedByIndex,
    ]);

    // HACK - should come up with a better to accomplish this (same as ModelResponseSelector)
    useEffect(() => {
      if (complete) {
        return;
      }
      if (!chatPrompt || chatPrompt === submittedPrompt) {
        return;
      }
      if (response) {
        return;
      }

      async function validateAndSubmitPrompt() {
        setSubmittedPrompt(chatPrompt);
        const historyChopped = chatHistory.slice(0, -1);
        if (config.systemPrompt?.length) {
          historyChopped.push({
            role: MessageRole.System,
            content: config.systemPrompt,
          });
        }
      }
      validateAndSubmitPrompt();
    }, [
      step.params,
      chatPrompt,
      submittedPrompt,
      setSubmittedPrompt,
      chatHistory,
      complete,
      taskContext.assignmentId,
      taskContext.projectId,
      config.systemPrompt,
      config.systemPromptGptOnly,
      response,
    ]);

    return (
      <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
        <div className="w-full">
          <CardAlert
            message={response && resultChanged ? 'Please save the section below.' : undefined}
          >
            <StepCard readOnly={readOnly}>
              <StepCard.Header>
                <StepCard.HeaderLeft>
                  <StepCard.Icon icon="award" />
                  <StepCard.HeaderColumn>
                    <StepCard.Title>
                      {step.title || DEFAULT_SEARCH_AUTOCOMPLETE_TITLE}
                    </StepCard.Title>
                    <StepCard.Subtitle>
                      {step.subtitle || DEFAULT_SEARCH_AUTOCOMPLETE_SUBTITLE}
                    </StepCard.Subtitle>
                  </StepCard.HeaderColumn>
                </StepCard.HeaderLeft>
                <StepCard.HeaderRight>
                  <CommentButton
                    visible={true}
                    childId={constructStepChildId(step)}
                    turnIndex={step.turn}
                  />
                  <ResponseTemplatingButton
                    childId={constructStepChildId(step)}
                    metadata={{ stepIndex: responseIndex }}
                  />
                  <PinButton responseIndex={responseIndex} />
                </StepCard.HeaderRight>
              </StepCard.Header>
              <StepCard.Body>
                <ReferencesSection
                  references={references}
                  setReferences={setReferences}
                  readOnly={!!readOnly}
                  handleAddSearchResultAction={handleAddSearchResultAction}
                  handleEditSearchResultAction={handleEditSearchResultAction}
                  handleRemoveSearchResultAction={handleRemoveSearchResultAction}
                  handleActionReorder={handleActionReorder}
                />
              </StepCard.Body>
              <StepCard.Footer className="flex gap-2 justify-end mt-4">
                <Tooltip
                  text="Search for  references"
                  side="top"
                  target={
                    <div>
                      <Button
                        onClick={() => {
                          setReferences([
                            ...references,
                            {
                              searchTerm: '',
                              searchJustification: '',
                              searchResults: [],
                            },
                          ]);
                        }}
                        disabled={readOnly}
                        variant="secondary"
                      >
                        Add Reference
                      </Button>
                    </div>
                  }
                />
                {!taskContext.previewMode && (
                  <div className="flex gap-2 justify-end mt-4">
                    <Tooltip
                      text={submitTooltip}
                      side="top"
                      disabled={!submitTooltip}
                      target={
                        <div>
                          <Button
                            onClick={() => {
                              onSubmit(
                                {
                                  type: InteractionStepType.SearchReferences,
                                  context: {
                                    prompt: chatPrompt,
                                    references,
                                  },
                                  output: '',
                                },
                                responseIndex,
                                step,
                                undefined,
                                true, // skip linting during submission
                              );
                            }}
                            disabled={submitTooltip.length > 0 || !resultChanged || readOnly}
                          >
                            {t('save-and-continue')}
                          </Button>
                        </div>
                      }
                    />
                  </div>
                )}
              </StepCard.Footer>
            </StepCard>
          </CardAlert>
        </div>
      </StepWidthWrapper>
    );
  };

export default memo(SearchReferencesTaskComponent);
